import { useMemo } from 'react'

import type { Filter } from '@cubejs-client/core'
import { CubeOperator, resultSetToTableData } from 'cube'
import { DRIFT_CHAT_OPPORTUNITY_OVERVIEW } from 'cube/constants'

import { CubeQuery, CubeQueryBuilder, useCubeQuery } from '@driftt/dds.reports'

import { resolveCubeKPIResults } from 'components/KPICard'
import {
  OpportunitiesAmountSumQuery,
  OpportunityAmountSum,
  OpportunityCountBaseQuery,
} from 'layouts/RevenueReport'
import { useOverviewStore } from 'layouts/RevenueReport/Overview/Overview.context'

import { getInfluencedAndSourcedFilters } from '../RevenueKPICards.helpers'

export interface IRevenuePipeline {
  opportunityPipelineAmountSum: number
  influencedOpportunityPipelineCount: number
  opportunityTotalCount: number
  isLoading: boolean
  hasNoData: boolean
  hasError: boolean
  opportunityPipelineAmountSumCubeQuery: CubeQuery
}

const contactEngaged: Filter = {
  member: DRIFT_CHAT_OPPORTUNITY_OVERVIEW.CONTACT_COUNT,
  operator: CubeOperator.GREATER_THAN,
  values: ['0'],
}

export const useRevenuePipelineKPI = ({
  isInfluenced = false,
  isSourced = false,
}): IRevenuePipeline => {
  const {
    store: { startDate, endDate },
  } = useOverviewStore()

  const kpiFilters: Filter[] = getInfluencedAndSourcedFilters({
    isInfluenced,
    isSourced,
    initialFilters: [contactEngaged],
  })

  const totalOpportunityCountCubeQuery = new CubeQueryBuilder({
    query: OpportunityCountBaseQuery,
  })
    .withName('OpportunitiesTotalCount')
    .withFilters([])
    .withinDateRange(
      DRIFT_CHAT_OPPORTUNITY_OVERVIEW.OPPORTUNITY_CREATED_AT,
      startDate,
      endDate
    )
    .build()

  const influencedOpportunityPipelineCountCubeQuery = new CubeQueryBuilder({
    query: OpportunityCountBaseQuery,
  })
    .withName('OpportunitiesPipelineCount')
    .withFilters(kpiFilters)
    .withinDateRange(
      DRIFT_CHAT_OPPORTUNITY_OVERVIEW.OPPORTUNITY_CREATED_AT,
      startDate,
      endDate
    )
    .build()

  const opportunityPipelineAmountSumCubeQuery = new CubeQueryBuilder({
    query: OpportunitiesAmountSumQuery,
  })
    .withQueryName('OpportunitiesAmountSumQuery')
    .withFilters(kpiFilters)
    .withinDateRange(
      DRIFT_CHAT_OPPORTUNITY_OVERVIEW.OPPORTUNITY_CREATED_AT,
      startDate,
      endDate
    )
    .build()

  const {
    data: influencedOpportunityPipelineCountResultSet,
    isLoading: isLoadingInfluencedOpportunityPipelineCount,
    isError: isErrorInfluencedOpportunityPipelineCount,
  } = useCubeQuery({
    query: influencedOpportunityPipelineCountCubeQuery,
  })

  const {
    data: totalOpportunityCountResultSet,
    isLoading: isLoadingTotalOpportunityCount,
    isError: isErrorTotalOpportunityCount,
  } = useCubeQuery({
    query: totalOpportunityCountCubeQuery,
  })

  const {
    data: resultSetPipelineAmountSum,
    isLoading: isLoadingPipelineAmountSum,
    isError: isErrorPipelineAmountSum,
  } = useCubeQuery({
    query: opportunityPipelineAmountSumCubeQuery,
  })

  const influencedOpportunityPipelineCount = useMemo(
    () =>
      resolveCubeKPIResults(
        influencedOpportunityPipelineCountResultSet,
        DRIFT_CHAT_OPPORTUNITY_OVERVIEW.COUNT ?? ''
      ) || 0,
    [influencedOpportunityPipelineCountResultSet]
  )

  const opportunityTotalCount = useMemo(() => {
    const totalOpp =
      resolveCubeKPIResults(
        totalOpportunityCountResultSet,
        DRIFT_CHAT_OPPORTUNITY_OVERVIEW.COUNT ?? ''
      ) || 0

    return (Number(influencedOpportunityPipelineCount) * 100) / Number(totalOpp)
  }, [influencedOpportunityPipelineCount, totalOpportunityCountResultSet])

  const opportunityPipelineAmountSum = useMemo(() => {
    const results: OpportunityAmountSum[] = resultSetToTableData(
      resultSetPipelineAmountSum
    ) as OpportunityAmountSum[]
    return Number(results[0]?.sum)
  }, [resultSetPipelineAmountSum])

  const isLoading = useMemo(
    () =>
      isLoadingInfluencedOpportunityPipelineCount &&
      isLoadingTotalOpportunityCount &&
      isLoadingPipelineAmountSum,
    [
      isLoadingInfluencedOpportunityPipelineCount,
      isLoadingTotalOpportunityCount,
      isLoadingPipelineAmountSum,
    ]
  )

  const hasNoData = useMemo(
    () =>
      !isLoading &&
      !influencedOpportunityPipelineCount &&
      !opportunityTotalCount,
    [isLoading, influencedOpportunityPipelineCount, opportunityTotalCount]
  )

  const hasError = useMemo(
    () =>
      !isLoading &&
      (isErrorInfluencedOpportunityPipelineCount ||
        isErrorTotalOpportunityCount ||
        isErrorPipelineAmountSum),
    [
      isLoading,
      isErrorInfluencedOpportunityPipelineCount,
      isErrorTotalOpportunityCount,
      isErrorPipelineAmountSum,
    ]
  )

  return useMemo(
    () => ({
      opportunityPipelineAmountSum,
      influencedOpportunityPipelineCount:
        influencedOpportunityPipelineCount || 0,
      opportunityTotalCount: opportunityTotalCount || 0,
      hasNoData,
      hasError,
      isLoading,
      opportunityPipelineAmountSumCubeQuery,
    }),
    [
      hasError,
      hasNoData,
      isLoading,
      opportunityPipelineAmountSum,
      influencedOpportunityPipelineCount,
      opportunityTotalCount,
      opportunityPipelineAmountSumCubeQuery,
    ]
  )
}

export default useRevenuePipelineKPI
